<script setup lang="ts">
import type { AppScrollbarProps } from '@/shared/ui/AppScrollbar/model/types/scrollbar';

const props = withDefaults(defineProps<AppScrollbarProps>(), {
  height: '400',
});

const isMobile: boolean = useIsMobile();

const emit = defineEmits<{
  (e: 'on-scroll', event: Event): void;
}>();

const styles: ComputedRef<Record<string, string>> = computed(() => ({
  height: `${props.height}px`,
}));

const MIN_THUMB_HEIGHT = 24;

const rootRef = useTemplateRef<HTMLElement>('rootRef');
const thumbTop: Ref<number> = ref(0);
const thumbHeight: Ref<number> = ref(0);
const isThumbVisible: Ref<boolean> = ref(false);

let thumbRafId: number | null = null;
let resizeObserver: ResizeObserver | null = null;
let mutationObserver: MutationObserver | null = null;

const thumbStyles: ComputedRef<Record<string, string>> = computed(() => ({
  top: `${thumbTop.value}px`,
  height: `${thumbHeight.value}px`,
}));

const updateThumb = (): void => {
  const scrollableEl = rootRef.value;
  if (!isMobile || !scrollableEl) return;

  const { scrollTop, scrollHeight, clientHeight } = scrollableEl;
  if (scrollHeight <= clientHeight) {
    isThumbVisible.value = false;
    return;
  }

  const height = Math.max((clientHeight / scrollHeight) * clientHeight, MIN_THUMB_HEIGHT);
  const maxThumbOffset = clientHeight - height;
  isThumbVisible.value = true;
  thumbHeight.value = height;
  thumbTop.value = (scrollTop / (scrollHeight - clientHeight)) * maxThumbOffset;
};

const onScroll = (event: Event) => {
  updateThumb();
  emit('on-scroll', event);
};

const scheduleThumbUpdate = (): void => {
  if (thumbRafId !== null) return;
  thumbRafId = requestAnimationFrame(() => {
    thumbRafId = null;
    updateThumb();
  });
};

onMounted(() => {
  const scrollableEl = rootRef.value;
  if (!isMobile || !scrollableEl) return;

  resizeObserver = new ResizeObserver(updateThumb);
  resizeObserver.observe(scrollableEl);
  mutationObserver = new MutationObserver(scheduleThumbUpdate);
  mutationObserver.observe(scrollableEl, {
    childList: true,
    subtree: true,
    characterData: true,
  });
});

onUnmounted(() => {
  if (thumbRafId !== null) cancelAnimationFrame(thumbRafId);
  resizeObserver?.disconnect();
  mutationObserver?.disconnect();
});
</script>

<template>
  <div
    ref="rootRef"
    :class="['app-scrollbar', { _mobile: isMobile }]"
    data-testid="scrollbar"
    :style="styles"
    @scroll.passive="onScroll"
  >
    <div v-if="isMobile" class="app-scrollbar__track">
      <div
        v-if="isThumbVisible"
        class="app-scrollbar__thumb"
        data-testid="scrollbar-thumb"
        :style="thumbStyles"
      ></div>
    </div>
    <slot></slot>
  </div>
</template>

<style lang="less">
.app-scrollbar {
  position: relative;
  z-index: 10;
  width: 100%;
  overflow-y: auto;
  overflow-x: hidden;

  &::-webkit-scrollbar {
    width: @size-x2;
    height: @size-x2;
  }

  &::-webkit-scrollbar-track {
    background-color: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: @color-gallery;
    border: 2px solid transparent;
    background-clip: padding-box;
    border-radius: @size-x2;
    cursor: pointer;
    transition:
      background-color 0.4s cubic-bezier(0.4, 0, 0.2, 1),
      border-color 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: @color-silver-chalice;
    border-color: @color-silver-chalice;
  }

  &._mobile {
    scrollbar-width: none;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  &__track {
    position: sticky;
    top: 0;
    height: 0;
    z-index: 11;
  }

  &__thumb {
    position: absolute;
    right: 2px;
    width: @main-size;
    border-radius: @size-x2;
    background-color: @color-alto;
  }
}
</style>
